// ONE definition of "the number a client may see" for the wallet.
//
// ONE QUESTION, ONE FIGURE (owner ruling 2026-07-19 — verbatim):
//
//   availableBalance()  — THE figure. The client's money, minus the
//                         commitments that are genuinely wallet-related:
//                         pending booking requests and requested withdrawals.
//                         Both of those are the client's OWN dirhams set aside
//                         for something the client themselves started, so
//                         netting them is honest.
//
//                             available = total − reserved_for_pending − pending_payouts
//
//                         This is the headline AND the spend gate AND the
//                         ceiling for every wallet contribution. There is no
//                         second, lower "usable" number anywhere.
//
// Due price adjustments (money the client OWES us) NEVER deduct from, net
// into, gate, or ceiling this figure. An adjustment is not wallet money set
// aside: it is a claim raised from outside, it may be settled by card or ride
// on an invoice and never touch the wallet at all. It stays fully VISIBLE
// (amountOwed() — the "Amount owed" tile and the itemised screens) and fully
// PAYABLE (wallet / card / invoice), but a "spendable = available − owed"
// figure is the bug this file exists to prevent, not a contract to restore.
// The old gate double-subtracted the very adjustment being paid: a client
// holding AED 350 who owed AED 304.50 was told the wallet could contribute
// NOTHING toward paying that same 304.50.
//
// NEGATIVES ARE SHOWN, NOT HIDDEN (2026-07-18, overruling the earlier
// "never show a client a negative headline" rule). displayAvailable() no longer
// floors at zero: a client whose pending requests exceed their balance sees the
// real negative figure plus a plain sentence naming what is committed. A zero
// where the truth is −600 is not a kindness, it is a wrong number — and it was
// contradicted on screen anyway by the committed lines sitting right beneath it.

const round2 = (n) => Math.round(Number(n || 0) * 100) / 100;

// The server's raw available_balance. On the CURRENT server this figure is
// total − reserved − payouts, free of due adjustments; on LEGACY deploys the
// server had subtracted due adjustments out of it (and did not send
// spendable_balance). Both are handled below so this file is correct against
// either, in either deploy order.
const rawAvailable = (wallet) => {
    if (!wallet) return 0;
    const a = Number(wallet.available_balance);
    if (Number.isFinite(a)) return a;
    // Falls back to total only when the server predates available_balance
    // entirely (never as a "nicer number" fallback).
    return Number(wallet.total_balance || 0);
};

// Money the client OWES: due (pending/approved) price adjustments. Never netted
// into the wallet headline — it gets its own labelled "Amount owed" figure.
export const amountOwed = (wallet) =>
    round2(wallet?.amount_owed_total ?? wallet?.due_adjustments_total ?? 0);

export const amountOwedCount = (wallet) => Number(wallet?.due_adjustments_count || 0);

export const isInDebt = (wallet) => amountOwed(wallet) > 0.005;

// True when the server keeps "owed" out of available_balance. Detected by the
// PRESENCE of the spendable_balance wire field, not by version: every server
// that sends it also sends an adjustment-free available_balance. (The field's
// VALUE is never read here any more — on current servers it is identical to
// available_balance and exists only for wire compatibility; on the brief
// deploys where it was lower, that lower number was the "available − owed"
// gate the owner killed on 2026-07-19.)
const serverSplitsOwed = (wallet) =>
    wallet?.spendable_balance != null && Number.isFinite(Number(wallet.spendable_balance));

// THE FIGURE — wallet money minus wallet-related commitments only
// (total − reserved_for_pending − pending_payouts). Legitimately negative when
// pending requests + withdrawals exceed the balance. This is the headline and
// the spend gate: due adjustments never reduce it (2026-07-19).
export const availableBalance = (wallet) => {
    if (!wallet) return 0;
    const raw = rawAvailable(wallet);
    // Current contract: available_balance is already free of due adjustments.
    if (serverSplitsOwed(wallet)) return round2(raw);
    // Legacy contract (old deploys, no spendable_balance field): the server
    // subtracted due adjustments — add them back so the figure describes the
    // WALLET rather than the wallet-minus-a-debt.
    return round2(raw + amountOwed(wallet));
};

// DELETED CONCEPT, KEPT AS AN ALIAS. "Spendable" used to be
// available − dueAdjustments; the owner's ruling (2026-07-19, verbatim:
// "booking adjustments shouldn't be deducted from available wallet balance not
// now not later not ever") killed it. There is ONE spending figure and it is
// availableBalance() — under BOTH server contracts the gate equals the
// headline. The export survives only so no caller can resurrect a second,
// lower number by re-implementing it. Do not add arithmetic here, ever.
export const spendableBalance = availableBalance;

// The headline as rendered. NO LONGER CLAMPED — a negative available is a fact
// about the client's commitments and is shown as such, paired with
// overcommittedExplanation() so the number never appears bare.
export const displayAvailable = (wallet) => availableBalance(wallet);

// A non-negative ceiling for arithmetic that must not go through zero (e.g.
// "how much can the wallet contribute to this charge"). max(0, available) —
// nothing else is ever subtracted (a due adjustment does not ceiling a wallet
// contribution; it is a separate obligation). This is a CEILING ON AN ACTION,
// not a statement about the client's money — never render it as the balance.
export const walletPortionCeiling = (wallet) => Math.max(0, availableBalance(wallet));

export const isOvercommitted = (wallet) => availableBalance(wallet) < -0.005;

export const shortfallAmount = (wallet) => Math.max(0, -availableBalance(wallet));

export const totalBalance = (wallet) => Number(wallet?.total_balance || 0);

// The committed lines whose amounts explain total − available. Each is
// GROSS (deposit-headroom rule: no netted mystery numbers in client UIs) and
// only included when nonzero, so the list is never padded with dashes.
//
// Due price adjustments are deliberately NOT here any more. WalletCommitted's
// copy promises these lines "stay yours" — true of a reservation and a pending
// withdrawal, false of a debt. Owed money is rendered separately.
export const committedBreakdown = (wallet) => {
    if (!wallet) return [];
    const rows = [];
    const push = (label, amount, count) => {
        const n = Number(amount || 0);
        if (n > 0.005) rows.push({ label, amount: n, count: Number(count) || 0 });
    };
    push('Pending booking requests', wallet.reserved_for_pending, wallet.pending_bookings_count);
    push('Pending withdrawals', wallet.pending_payouts_total, wallet.pending_payouts_count);
    return rows;
};

// Total of everything committed — equals total − available when the server's
// breakdown is complete.
export const committedTotal = (wallet) =>
    round2(committedBreakdown(wallet).reduce((s, r) => s + r.amount, 0));

export const fmtAed = (n) =>
    `AED ${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// The plain sentence that must accompany a negative headline. Returns null when
// there is nothing to explain, so callers can render it unconditionally.
export const overcommittedExplanation = (wallet) => {
    if (!isOvercommitted(wallet)) return null;
    const causes = committedBreakdown(wallet)
        .map((r) => r.label.replace(/^Pending /, '').toLowerCase())
        .join(' and ');
    return `Your balance is ${fmtAed(totalBalance(wallet))}. Your ${causes || 'pending requests'} `
        + `commit ${fmtAed(committedTotal(wallet))}, so you are ${fmtAed(shortfallAmount(wallet))} short — `
        + `top up before they are approved.`;
};
